'use client';

import { c as _c } from "react/compiler-runtime";
import { type useGetSearch, usePrefetchGetMarket, usePrefetchGetOutcomeChart, usePrefetchGetProposal, usePrefetchGetUser, usePrefetchGetWinner } from '@morfi/backend-sdk-v2/client';
import { useFormatter } from 'next-intl';
import { useEffect, useRef, useState } from 'react';
import { useLiveClock } from '@/hooks/live-events/use-live-clock';
import { usePublishedEventCatalog } from '@/hooks/live-events/use-published-event-catalog';
import { eventPhase } from '@/lib/live-events/map-published-event';
import { PROPOSAL_DETAIL_PREFETCH_FIELDS } from '@/lib/sdk/projections/launchpad';
import { PORTFOLIO_USER_PROFILE_FIELDS } from '@/lib/sdk/projections/portfolio';
import type { LiveEventSearchResult } from './live-event-row-search-item';
import type { RowSharedProps } from './search-modal-rows';
import type { BlendedSearchResult, CreatorSearchResult, MarketSearchResult, OutcomeSearchResult, ProposalSearchResult, SearchTab, UserSearchResult, WinnerSearchResult } from './search-types';
const DEBOUNCE_MS = 300;
export interface SearchActionsParams {
  query: string;
  tickerHighlighted: boolean;
  addRecent: (query: string) => void;
  setQuery: (query: string) => void;
  setDebouncedQuery: (query: string) => void;
  setSearchOpen: (open: boolean) => void;
}
export function useSearchActions(t0) {
  const $ = _c(34);
  const {
    query,
    tickerHighlighted,
    addRecent,
    setQuery,
    setDebouncedQuery,
    setSearchOpen
  } = t0;
  const prefetchMarket = usePrefetchGetMarket();
  const prefetchProposal = usePrefetchGetProposal();
  const prefetchUser = usePrefetchGetUser();
  const prefetchWinner = usePrefetchGetWinner();
  const prefetchOutcomeChart = usePrefetchGetOutcomeChart();
  let t1;
  if ($[0] !== prefetchMarket) {
    t1 = function onHoverMarket(pubkey) {
      prefetchMarket({
        pubkey
      });
    };
    $[0] = prefetchMarket;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const onHoverMarket = t1;
  let t2;
  if ($[2] !== prefetchOutcomeChart || $[3] !== prefetchWinner) {
    t2 = function onHoverWinner(outcomeMint) {
      prefetchWinner({
        outcomeMint
      });
      prefetchOutcomeChart({
        outcomeMint
      });
    };
    $[2] = prefetchOutcomeChart;
    $[3] = prefetchWinner;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const onHoverWinner = t2;
  let t3;
  if ($[5] !== prefetchProposal) {
    t3 = function onHoverProposal(pubkey_0) {
      prefetchProposal({
        pubkey: pubkey_0,
        fields: PROPOSAL_DETAIL_PREFETCH_FIELDS
      });
    };
    $[5] = prefetchProposal;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const onHoverProposal = t3;
  let t4;
  if ($[7] !== prefetchUser) {
    t4 = function onHoverUser(identifier) {
      prefetchUser({
        identifier,
        fields: PORTFOLIO_USER_PROFILE_FIELDS
      });
    };
    $[7] = prefetchUser;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const onHoverUser = t4;
  let t5;
  if ($[9] !== addRecent || $[10] !== query || $[11] !== setSearchOpen) {
    t5 = function commitSearch() {
      if (query.trim().length > 0) {
        addRecent(query);
      }
      setSearchOpen(false);
    };
    $[9] = addRecent;
    $[10] = query;
    $[11] = setSearchOpen;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const commitSearch = t5;
  let t6;
  if ($[13] !== setDebouncedQuery || $[14] !== setQuery) {
    t6 = function handleRecentSelect(recentQuery) {
      setQuery(recentQuery);
      setDebouncedQuery(recentQuery);
    };
    $[13] = setDebouncedQuery;
    $[14] = setQuery;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const handleRecentSelect = t6;
  let t7;
  if ($[16] !== setSearchOpen) {
    t7 = function handleOpenChange(open) {
      setSearchOpen(open);
    };
    $[16] = setSearchOpen;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const handleOpenChange = t7;
  let t8;
  if ($[18] !== setDebouncedQuery || $[19] !== setQuery) {
    t8 = async function handlePaste() {
      try {
        const text = await navigator.clipboard.readText();
        const trimmed = text.trim();
        if (trimmed.length > 0) {
          setQuery(trimmed);
          setDebouncedQuery(trimmed);
        }
      } catch {}
    };
    $[18] = setDebouncedQuery;
    $[19] = setQuery;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  const handlePaste = t8;
  let t9;
  if ($[21] !== commitSearch || $[22] !== onHoverMarket || $[23] !== onHoverProposal || $[24] !== onHoverUser || $[25] !== onHoverWinner || $[26] !== tickerHighlighted) {
    t9 = {
      tickerHighlighted,
      onCommit: commitSearch,
      onHoverMarket,
      onHoverWinner,
      onHoverProposal,
      onHoverUser
    };
    $[21] = commitSearch;
    $[22] = onHoverMarket;
    $[23] = onHoverProposal;
    $[24] = onHoverUser;
    $[25] = onHoverWinner;
    $[26] = tickerHighlighted;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  let t10;
  if ($[28] !== commitSearch || $[29] !== handleOpenChange || $[30] !== handlePaste || $[31] !== handleRecentSelect || $[32] !== t9) {
    t10 = {
      rowShared: t9,
      commitSearch,
      handleRecentSelect,
      handleOpenChange,
      handlePaste
    };
    $[28] = commitSearch;
    $[29] = handleOpenChange;
    $[30] = handlePaste;
    $[31] = handleRecentSelect;
    $[32] = t9;
    $[33] = t10;
  } else {
    t10 = $[33];
  }
  return t10;
}
export function useSearchInputState(liveEventsEnabled, searchInitialTab) {
  const $ = _c(7);
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [selectedTab, setSelectedTab] = useState(null);
  const activeTab = selectedTab ?? (liveEventsEnabled ? searchInitialTab : "all");
  const debounceTimerRef = useRef(null);
  let t0;
  let t1;
  if ($[0] !== query) {
    t0 = () => {
      if (debounceTimerRef.current !== null) {
        clearTimeout(debounceTimerRef.current);
      }
      debounceTimerRef.current = setTimeout(() => {
        debounceTimerRef.current = null;
        setDebouncedQuery(query);
      }, DEBOUNCE_MS);
      return () => {
        if (debounceTimerRef.current !== null) {
          clearTimeout(debounceTimerRef.current);
        }
      };
    };
    t1 = [query];
    $[0] = query;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
  let t2;
  if ($[3] !== activeTab || $[4] !== debouncedQuery || $[5] !== query) {
    t2 = {
      query,
      debouncedQuery,
      activeTab,
      setQuery,
      setDebouncedQuery,
      setActiveTab: setSelectedTab
    };
    $[3] = activeTab;
    $[4] = debouncedQuery;
    $[5] = query;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
}
export function useLiveEventSearchCatalog(t0) {
  const $ = _c(9);
  const {
    liveEvents,
    liveEventsEnabled,
    searchOpen,
    shouldSearch,
    cleanQuery
  } = t0;
  const catalogRequested = liveEvents === undefined && liveEventsEnabled;
  const catalog = useIndexedLiveEventSearchCatalog(catalogRequested && searchOpen && shouldSearch);
  const t1 = liveEvents ?? catalog.rows;
  const t2 = liveEventsEnabled && shouldSearch;
  let t3;
  if ($[0] !== cleanQuery || $[1] !== t1 || $[2] !== t2) {
    t3 = matchingEventTitles(t1, t2, cleanQuery);
    $[0] = cleanQuery;
    $[1] = t1;
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const t4 = catalogRequested && catalog.isPending;
  const t5 = catalogRequested && catalog.isError;
  let t6;
  if ($[4] !== catalog.retry || $[5] !== t3 || $[6] !== t4 || $[7] !== t5) {
    t6 = {
      matchingLiveEvents: t3,
      eventCatalogPending: t4,
      eventCatalogError: t5,
      retryEventCatalog: catalog.retry
    };
    $[4] = catalog.retry;
    $[5] = t3;
    $[6] = t4;
    $[7] = t5;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  return t6;
}
function useIndexedLiveEventSearchCatalog(enabled) {
  const $ = _c(12);
  const format = useFormatter();
  const nowMs = useLiveClock(1000, enabled);
  const catalog = usePublishedEventCatalog(enabled, true);
  const {
    rows
  } = catalog;
  let t0;
  if ($[0] !== format || $[1] !== nowMs || $[2] !== rows) {
    let t1;
    if ($[4] !== format || $[5] !== nowMs) {
      t1 = event => ({
        id: event.eventId,
        title: event.title,
        imageUrl: event.bannerImage.url,
        phase: eventPhase(event, nowMs),
        dateLabel: format.dateTime(new Date(event.tradingStartAt), {
          dateStyle: "medium",
          timeZone: event.timezone
        })
      });
      $[4] = format;
      $[5] = nowMs;
      $[6] = t1;
    } else {
      t1 = $[6];
    }
    t0 = rows.map(t1);
    $[0] = format;
    $[1] = nowMs;
    $[2] = rows;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const t1 = nowMs === 0 || catalog.isPending;
  let t2;
  if ($[7] !== catalog.isError || $[8] !== catalog.retry || $[9] !== t0 || $[10] !== t1) {
    t2 = {
      rows: t0,
      isPending: t1,
      isError: catalog.isError,
      retry: catalog.retry
    };
    $[7] = catalog.isError;
    $[8] = catalog.retry;
    $[9] = t0;
    $[10] = t1;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  return t2;
}
function matchingEventTitles(events: readonly LiveEventSearchResult[], enabled: boolean, query: string): readonly LiveEventSearchResult[] {
  if (!enabled) return [];
  const titleQuery = query.toLowerCase();
  return events.filter(event => event.title.toLowerCase().includes(titleQuery));
}
export interface SearchCollections {
  blended: readonly BlendedSearchResult[];
  markets: readonly MarketSearchResult[];
  outcomes: readonly OutcomeSearchResult[];
  winners: readonly WinnerSearchResult[];
  proposals: readonly ProposalSearchResult[];
  creators: readonly CreatorSearchResult[];
  users: readonly UserSearchResult[];
  liveEvents: readonly LiveEventSearchResult[];
}
export function hasTabResults(tab: SearchTab, results: Omit<SearchCollections, 'blended' | 'creators'>): boolean {
  switch (tab) {
    case 'markets':
      return results.markets.length > 0;
    case 'outcomes':
      return results.outcomes.length > 0;
    case 'live-events':
      return results.liveEvents.length > 0;
    case 'winners':
      return results.winners.length > 0;
    case 'proposals':
      return results.proposals.length > 0;
    case 'users':
      return results.users.length > 0;
    case 'all':
      return false;
  }
}
export function deriveSearchResultState(results: SearchCollections, activeTab: SearchTab, mode: string, cleanQuery: string): {
  hasResults: boolean;
  hasExactMatch: boolean;
  exactMatchPubkey: string | null;
  activeTabHasResults: boolean;
} {
  const hasResults = Object.values(results).some(group => group.length > 0);
  const exactMatchPubkey = mode === 'ca' ? cleanQuery : null;
  const hasExactMatch = hasExactSearchMatch(results, exactMatchPubkey);
  const activeTabHasResults = activeTab === 'all' ? mode === 'ca' ? hasExactMatch : hasFuzzySearchResults(results) : hasTabResults(activeTab, results);
  return {
    hasResults,
    hasExactMatch,
    exactMatchPubkey,
    activeTabHasResults
  };
}
function hasExactSearchMatch(results: SearchCollections, pubkey: string | null): boolean {
  if (pubkey === null) return false;
  return results.markets.some(market => market.pubkey === pubkey) || results.outcomes.some(outcome => outcome.pubkey === pubkey) || results.winners.some(winner => winner.pubkey === pubkey || winner.mint === pubkey) || results.proposals.some(proposal => proposal.proposalId === pubkey) || results.users.some(user => user.user.walletAddress === pubkey);
}
function hasFuzzySearchResults(results: SearchCollections): boolean {
  return [results.blended, results.outcomes, results.winners, results.creators, results.users, results.liveEvents].some(group => group.length > 0);
}
const EMPTY_SEARCH_RESULTS: Omit<SearchCollections, 'liveEvents'> = {
  blended: [],
  markets: [],
  outcomes: [],
  winners: [],
  proposals: [],
  creators: [],
  users: []
};
export function searchResultRows(data: ReturnType<typeof useGetSearch>['data']): Omit<SearchCollections, 'liveEvents'> {
  return data ?? EMPTY_SEARCH_RESULTS;
}
export function searchLoadingState(activeTab: SearchTab, mode: string, shouldSearch: boolean, isPending: boolean, eventCatalogPending: boolean, eventCatalogError: boolean): {
  eventsRelevant: boolean;
  searchPending: boolean;
  isLoading: boolean;
} {
  const eventsRelevant = includesLiveEventResults(activeTab, mode);
  const waitingForEvents = eventCatalogPending || eventCatalogError;
  const searchPending = activeTab === 'live-events' ? waitingForEvents : isPending || eventsRelevant && waitingForEvents;
  return {
    eventsRelevant,
    searchPending,
    isLoading: shouldSearch && searchPending && !(eventsRelevant && eventCatalogError)
  };
}
function includesLiveEventResults(tab: SearchTab, mode: string): boolean {
  return tab === 'live-events' || tab === 'all' && mode !== 'ca';
}